import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Button } from '@trellis/roots/button'
import { Typography } from '@trellis/roots/typography'
import { FilePlus, ListChecks, Search } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useDisclosure } from '@/hooks'
import type { LineItemErrors } from '@/hooks/useLineItemValidation'

import type { LineItemRowValue } from '../pages/invoices/(components)/LineItemRow'
import { RegistryItemBrowserSheet } from '../pages/invoices/(components)/RegistryItemBrowserDialog'
import { SortableLineItemRow } from '../pages/invoices/(components)/SortableLineItemRow'

export type SortableLineItem = LineItemRowValue & { _id: string }

export function withId(lineItem: LineItemRowValue): SortableLineItem {
  return { ...lineItem, _id: crypto.randomUUID() }
}

const BLANK_ADHOC_ROW: LineItemRowValue = {
  type: 'adhoc',
  title: '',
  description: null,
  unitPrice: 0,
  quantity: 1,
  unitType: 'HOUR',
  discountType: 'NONE',
  discount: 0,
  taxable: true,
  saveToRegistry: true,
}

interface DocumentLineItemsSectionProps {
  value: SortableLineItem[]
  onChange: (items: SortableLineItem[]) => void
  currency: string
  /**
   * Field errors per row `_id`, from {@link useLineItemValidation}. Empty until a save is
   * attempted.
   */
  errors?: LineItemErrors
  /**
   * Whether the surrounding form is editing a previously-saved document, not creating a
   * new one.
   */
  isEditMode: boolean
}

export function DocumentLineItemsSection({
  value: lineItems,
  onChange: setLineItems,
  currency,
  errors = {},
  isEditMode,
}: DocumentLineItemsSectionProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const registrySheet = useDisclosure()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const invalidRowIds = Object.keys(errors)

  // Auto-expand rows a failed save flagged, so the user sees why without hunting for them.
  useEffect(() => {
    const flaggedIds = Object.keys(errors)
    if (flaggedIds.length === 0) return
    setExpandedIds((previous) => new Set([...previous, ...flaggedIds]))
  }, [errors])

  function handleDragEnd(dragEndEvent: DragEndEvent) {
    const { active, over } = dragEndEvent
    if (over && active.id !== over.id) {
      setLineItems(
        arrayMove(
          lineItems,
          lineItems.findIndex((lineItem) => lineItem._id === active.id),
          lineItems.findIndex((lineItem) => lineItem._id === over.id),
        ),
      )
    }
  }

  return (
    <div className="bg-card rounded-xl border shadow-sm">
      <div className="flex items-center justify-between border-b px-6 py-4">
        <div>
          <div className="flex items-center gap-1.5">
            <ListChecks className="size-4" aria-hidden="true" />
            <Typography variant="h5" as="h3">
              Line items
            </Typography>
          </div>
          <Typography variant="body-sm" color="muted" className="mt-1">
            Type a title to find registry items, or add a custom item.
          </Typography>
          {invalidRowIds.length > 0 && (
            <Typography variant="body-sm" color="destructive-text" className="mt-1">
              {invalidRowIds.length === 1
                ? '1 line item is missing required details — see below.'
                : `${invalidRowIds.length} line items are missing required details — see below.`}
            </Typography>
          )}
        </div>
      </div>

      <div className="space-y-3 px-6 py-5">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={lineItems.map((lineItem) => lineItem._id)}
            strategy={verticalListSortingStrategy}
          >
            {lineItems.map((lineItem, index) => (
              <SortableLineItemRow
                key={lineItem._id}
                id={lineItem._id}
                value={lineItem}
                index={index}
                currency={currency}
                errors={errors[lineItem._id] ?? {}}
                isEditMode={isEditMode}
                isExpanded={expandedIds.has(lineItem._id)}
                onToggleExpand={() =>
                  setExpandedIds((previous) => {
                    const next = new Set(previous)
                    if (next.has(lineItem._id)) {
                      next.delete(lineItem._id)
                    } else {
                      next.add(lineItem._id)
                    }
                    return next
                  })
                }
                onChange={(updatedValue) =>
                  setLineItems(
                    lineItems.map((previousItem, previousIndex) =>
                      previousIndex === index
                        ? { ...updatedValue, _id: previousItem._id }
                        : previousItem,
                    ),
                  )
                }
                onRemove={() => {
                  setLineItems(lineItems.filter((_, itemIndex) => itemIndex !== index))
                  setExpandedIds((previous) => {
                    const next = new Set(previous)
                    next.delete(lineItem._id)
                    return next
                  })
                }}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => {
              const newItem = withId({ ...BLANK_ADHOC_ROW })
              setLineItems([...lineItems, newItem])
              setExpandedIds((previous) => new Set([...previous, newItem._id]))
            }}
          >
            <FilePlus className="mr-1 h-4 w-4" />
            Add item
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => registrySheet.open(true)}
          >
            <Search className="mr-1 h-4 w-4" />
            Browse registry
          </Button>
        </div>

        <RegistryItemBrowserSheet
          open={registrySheet.isOpen}
          onOpenChange={registrySheet.onOpenChange}
          onAdd={(items) => setLineItems([...lineItems, ...items.map(withId)])}
        />
      </div>
    </div>
  )
}
